import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api, fmtMoney, fmtDate, fmtDateTime, STATUS_FLOW, STATUS_META, PAYMENT_STATUS_META } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { useAuth } from "@/contexts/AuthContext";
import {
  ArrowLeft, DownloadSimple, WhatsappLogo, Check, FileText, Receipt, PencilSimple,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function OrderDetail() {
  const { id } = useParams();
  const { hasRole, user } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [brands, setBrands] = useState([]);
  const [showSignStamp, setShowSignStamp] = useState(true);
  const [statusOpen, setStatusOpen] = useState(false);
  const [newStatus, setNewStatus] = useState("");
  const [statusNote, setStatusNote] = useState("");

  const load = async () => {
    const { data } = await api.get(`/orders/${id}`);
    setOrder(data);
    if (data.customer_id) {
      const c = await api.get(`/customers/${data.customer_id}`);
      setCustomer(c.data);
    }
  };
  useEffect(() => {
    load();
    api.get("/invoices/brands").then((r) => setBrands(r.data || [])).catch(() => {});
    /* eslint-disable-next-line */
  }, [id]);

  if (!order) return <div className="label-mini">Loading…</div>;

  const isQuotation = order.status === "quotation";
  const docType = isQuotation ? "quotation" : "invoice";
  const orderBrand = brands.find((b) => b.id === order.brand_id) || brands[0];

  const downloadPdf = async (which, brandId) => {
    try {
      const params = new URLSearchParams({ doc_type: which, show_sign_stamp: showSignStamp });
      if (brandId) params.set("brand_id", brandId);
      const res = await api.get(`/invoices/order/${id}/pdf?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `${order.code}-${brandId || "default"}-${which}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      load();
      toast.success("PDF generated");
    } catch (e) {
      toast.error("Failed to generate PDF");
    }
  };

  const confirmQuotation = async () => {
    try {
      await api.post(`/invoices/order/${id}/confirm`);
      toast.success("Quotation confirmed → Order");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const changeStatus = async () => {
    if (!newStatus) return;
    try {
      await api.post(`/orders/${id}/status`, { new_status: newStatus, notes: statusNote });
      toast.success("Status updated");
      setStatusOpen(false);
      setStatusNote("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    }
  };

  const waMessage = customer && customer.whatsapp
    ? `https://wa.me/${customer.whatsapp.replace(/\D/g, "")}?text=${encodeURIComponent(
        `Hi ${customer.name}, please find your ${isQuotation ? "quotation" : "invoice"} ${order.code} ready. Total: PKR ${(order.total_sell || 0).toFixed(2)}.`,
      )}`
    : null;

  const canEditStatus = hasRole("admin", "operations", "sales");

  return (
    <div className="space-y-5" data-testid="order-detail-page">
      <button onClick={() => navigate(-1)} className="text-xs label-mini flex items-center gap-1 hover:text-black">
        <ArrowLeft size={12} /> Back
      </button>

      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-mini">Order Record</div>
          <h1 className="text-3xl font-bold tracking-tight mt-1 flex items-center gap-3 flex-wrap">
            <span className="font-mono">{order.code}</span>
            {order.reference_no && <span className="text-sm font-normal text-zinc-500">Ref: <span className="font-mono">{order.reference_no}</span></span>}
            <StatusBadge status={order.status} />
            {(() => {
              const ps = PAYMENT_STATUS_META[order.payment_status] || PAYMENT_STATUS_META.unpaid;
              return (
                <span className="status-badge" style={{ background: ps.bg, color: ps.fg, borderColor: ps.bd }} data-testid="order-payment-badge">
                  {ps.label}
                </span>
              );
            })()}
            {orderBrand && (
              <span className="status-badge" style={{ background: "#0A0A0A", color: "white", borderColor: "#0A0A0A" }} data-testid="order-brand-badge">
                {orderBrand.short_name || orderBrand.name}
              </span>
            )}
          </h1>
        </div>

        <div className="flex items-center gap-2 flex-wrap" data-testid="order-actions">
          <label className="flex items-center gap-1.5 text-xs cursor-pointer select-none" data-testid="toggle-sign-stamp">
            <input type="checkbox" checked={showSignStamp} onChange={(e) => setShowSignStamp(e.target.checked)} />
            Sign &amp; stamp
          </label>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="rounded-sm bg-white text-black border border-black h-9 hover:bg-zinc-50" data-testid="download-pdf-btn">
                <DownloadSimple size={14} className="mr-1" /> {isQuotation ? "Quotation" : "Invoice"} PDF
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="rounded-sm w-64">
              <DropdownMenuLabel className="label-mini">Issue under brand</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {brands.map((b) => (
                <DropdownMenuItem key={b.id} onClick={() => downloadPdf(docType, b.id)} data-testid={`download-pdf-${b.id}`}>
                  <FileText size={14} className="mr-2" />
                  <div className="flex-1">
                    <div className="font-medium">{b.name}</div>
                    <div className="text-[10px] text-zinc-500 font-mono">{isQuotation ? b.quotation_prefix : b.invoice_prefix}</div>
                  </div>
                  {order.brand_id === b.id && (
                    <span className="text-[10px] label-mini text-emerald-700">Order brand</span>
                  )}
                </DropdownMenuItem>
              ))}
              {brands.length === 0 && (
                <DropdownMenuItem onClick={() => downloadPdf(docType)} data-testid="download-pdf-default">
                  <FileText size={14} className="mr-2" /> Default
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
          {waMessage && (
            <a href={waMessage} target="_blank" rel="noreferrer" data-testid="share-whatsapp-btn">
              <Button className="rounded-sm bg-emerald-700 hover:bg-emerald-800 text-white h-9">
                <WhatsappLogo size={14} className="mr-1" /> Share via WhatsApp
              </Button>
            </a>
          )}
          {isQuotation && hasRole("admin", "sales") && (
            <Button onClick={confirmQuotation} className="rounded-sm bg-black text-white hover:bg-zinc-800 h-9" data-testid="confirm-quotation-btn">
              <Check size={14} className="mr-1" /> Confirm Order
            </Button>
          )}
          {canEditStatus && order.status !== "delivered" && order.status !== "cancelled" && (
            <Button onClick={() => setStatusOpen(true)} variant="outline" className="rounded-sm h-9" data-testid="update-status-btn">
              <PencilSimple size={14} className="mr-1" /> Update Status
            </Button>
          )}
          {hasRole("admin", "sales") && order.status !== "cancelled" && (
            <Button onClick={() => navigate(`/orders/${id}/edit`)} variant="outline" className="rounded-sm h-9" data-testid="edit-order-btn">
              <PencilSimple size={14} className="mr-1" /> Edit Order
            </Button>
          )}
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-px bg-zinc-200 border border-zinc-200">
        <div className="bg-white p-5">
          <h3 className="label-mini">Customer</h3>
          {customer && (
            <div className="mt-2">
              <Link to={`/customers/${customer.id}`} className="font-semibold underline">{customer.name}</Link>
              <div className="text-xs text-zinc-500 mt-1 font-mono">{customer.code}</div>
              <div className="text-xs text-zinc-600 mt-1">{customer.phone}</div>
              <div className="text-xs text-zinc-600">{customer.email}</div>
              <div className="text-xs text-zinc-600">{customer.city}, {customer.country}</div>
            </div>
          )}
        </div>
        <div className="bg-white p-5">
          <h3 className="label-mini">Shipping</h3>
          <div className="mt-2 text-sm space-y-1">
            <Pair label="Method" value={(order.shipping_method || "").replace("_", " ")} />
            <Pair label="Tracking" value={order.tracking_no || "—"} />
            <Pair label="Expected" value={fmtDate(order.expected_delivery)} />
            <Pair label="Charged to" value={order.shipping_charged_to_customer ? "Customer" : "Business"} />
            <Pair label="Cost" value={fmtMoney(order.shipping_cost || 0)} />
          </div>
        </div>
        <div className="bg-white p-5">
          <h3 className="label-mini">Financial</h3>
          <div className="mt-2 space-y-2">
            {order.gst_percent > 0 && (
              <>
                <div className="flex items-center justify-between"><span className="text-xs text-zinc-500">Subtotal</span><span className="font-mono tnum text-sm">{fmtMoney(order.subtotal_sell || 0)}</span></div>
                <div className="flex items-center justify-between"><span className="text-xs text-zinc-500">GST @ {order.gst_percent}%</span><span className="font-mono tnum text-sm" data-testid="order-gst">{fmtMoney(order.gst_amount || 0)}</span></div>
              </>
            )}
            <div className="flex items-center justify-between"><span className="text-xs text-zinc-500">Selling</span><span className="font-mono tnum text-sm">{fmtMoney(order.total_sell || 0)}</span></div>
            <div className="flex items-center justify-between"><span className="text-xs text-zinc-500">Cost</span><span className="font-mono tnum text-sm">{fmtMoney(order.total_cost || 0)}</span></div>
            <div className="flex items-center justify-between"><span className="text-xs text-zinc-500">Paid</span><span className="font-mono tnum text-sm" data-testid="order-paid">{fmtMoney(order.paid_amount || 0)}</span></div>
            {order.status !== "cancelled" ? (
              <div className="flex items-center justify-between"><span className="text-xs text-zinc-500">Outstanding</span><span className="font-mono tnum text-sm font-semibold" data-testid="order-outstanding">{fmtMoney(Math.max(0, (order.total_sell || 0) - (order.paid_amount || 0)))}</span></div>
            ) : (order.paid_amount || 0) > 0 ? (
              <div className="flex items-center justify-between"><span className="text-xs text-red-700 font-semibold">Refund Due</span><span className="font-mono tnum text-sm font-semibold text-red-700" data-testid="order-refund">{fmtMoney(order.paid_amount || 0)}</span></div>
            ) : null}
            <div className="flex items-center justify-between border-t border-zinc-200 pt-2">
              <span className="text-xs font-semibold">Margin</span>
              <span className="font-mono tnum text-base font-bold" data-testid="order-margin" style={{ color: (order.margin || 0) >= 0 ? "#065F46" : "#B91C1C" }}>
                {fmtMoney(order.margin || 0)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="card-sharp p-5">
        <h3 className="label-mini">Line Items</h3>
        <div className="overflow-x-auto mt-2">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left label-mini border-b border-zinc-200">
                <th className="py-2">#</th>
                <th className="py-2">Description</th>
                <th className="py-2 text-right">Qty</th>
                <th className="py-2 text-right">Unit Cost</th>
                <th className="py-2 text-right">Unit Sell</th>
                <th className="py-2 text-right">Line Total (Sell)</th>
              </tr>
            </thead>
            <tbody>
              {(order.items || []).map((it, i) => (
                <tr key={it.id || i} className="border-b border-zinc-100">
                  <td className="py-2 font-mono text-xs">{i + 1}</td>
                  <td className="py-2 font-medium">{it.description}</td>
                  <td className="py-2 text-right font-mono tnum">{it.qty} {it.unit}</td>
                  <td className="py-2 text-right font-mono tnum">{fmtMoney(it.cost_price, it.cost_currency)}</td>
                  <td className="py-2 text-right font-mono tnum">{fmtMoney(it.sell_price, it.sell_currency)}</td>
                  <td className="py-2 text-right font-mono tnum font-semibold">{fmtMoney(Number(it.qty) * Number(it.sell_price), it.sell_currency)}</td>
                </tr>
              ))}
              {(order.items || []).length === 0 && (
                <tr><td colSpan={6} className="py-4 text-center text-xs text-zinc-500">No items.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Timeline */}
      <div className="card-sharp p-5">
        <h3 className="label-mini">Status History</h3>
        <div className="mt-3 space-y-3">
          {(order.status_history || []).slice().reverse().map((h, i) => (
            <div key={i} className="flex gap-3 items-start text-sm">
              <div className="h-2 w-2 mt-2 bg-black" />
              <div className="flex-1">
                <div className="font-medium">
                  {h.from ? `${STATUS_META[h.from]?.label || h.from} → ` : ""}
                  {STATUS_META[h.to]?.label || h.to}
                </div>
                <div className="text-xs text-zinc-500">{h.changed_by} · {fmtDateTime(h.at)}</div>
                {h.notes && <div className="text-xs text-zinc-700 mt-0.5">{h.notes}</div>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {order.customer_notes && (
        <div className="card-sharp p-5">
          <h3 className="label-mini">Customer-Facing Notes</h3>
          <div className="text-sm mt-2 whitespace-pre-line">{order.customer_notes}</div>
        </div>
      )}
      {order.internal_notes && hasRole("admin", "operations", "accounts", "sales") && (
        <div className="card-sharp p-5 bg-zinc-50">
          <h3 className="label-mini">Internal Notes (Not visible to customer)</h3>
          <div className="text-sm mt-2 whitespace-pre-line">{order.internal_notes}</div>
        </div>
      )}

      <Dialog open={statusOpen} onOpenChange={setStatusOpen}>
        <DialogContent className="rounded-sm">
          <DialogHeader><DialogTitle>Update Order Status</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <label className="label-mini">New Status</label>
              <Select value={newStatus} onValueChange={setNewStatus}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="status-select"><SelectValue placeholder="Choose status" /></SelectTrigger>
                <SelectContent>
                  {STATUS_FLOW.map((s) => (
                    <SelectItem key={s} value={s}>{STATUS_META[s].label}</SelectItem>
                  ))}
                  {hasRole("admin") && <SelectItem value="cancelled">Cancelled</SelectItem>}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="label-mini">Notes</label>
              <Input value={statusNote} onChange={(e) => setStatusNote(e.target.value)} className="rounded-sm mt-1" placeholder="e.g., shipped via DHL AWB 12345" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStatusOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={changeStatus} disabled={!newStatus} className="rounded-sm bg-black text-white hover:bg-zinc-800" data-testid="save-status-btn">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Pair = ({ label, value }) => (
  <div className="flex items-center justify-between">
    <span className="text-xs text-zinc-500 capitalize">{label}</span>
    <span className="text-sm capitalize">{value}</span>
  </div>
);
